import { computed, Ref, ref } from "@vue/composition-api";
import { fabric } from "fabric";
import { Canvas, IEvent, Textbox } from "fabric/fabric-impl";
import useColor from "./useColor";
import { colorList, FillBackgroundType, FontsType, ObjectType, TextAlign, textFonts } from "../../../../config";
import {
  BackgroundFromPath,
  BackgroundFromRect,
  BackgroundFromText,
  BackgroundObjects,
  ColorScheme,
  GroupInstance,
  ObjectsGroup,
  StyleScheme,
} from "../../../../types";

export default function useObjects(canvasInstance: Ref<Canvas | undefined>, resetControllers: Function) {
  const { textColor, backgroundColors, onChangeColorsState } = useColor();
  const DEFAULT_FONT_SIZE = 24;
  const showDeleteObjectIcon = ref(false);
  const startPositionTextObject = ref({ top: 0, left: 0 });
  /**
   * True когда мы в режиме редактирования жмем на любую строку редактируемого текста,
   *   это помогает не сбрасывать редактирование, когда жмешь по тексту
   * False при каждом нажатии на холст
   * */
  const cursorIsChanged = ref(false);

  /**
   * Добавляется:
   * - когда создаешь новый текст
   * - когда входишь в режим редактирования старого
   * Отчищается:
   * - когда выходишь из редактирования
   * - когда сохраняешь оверлей
   * */
  const editableTextObject = ref<fabric.Textbox>();
  /**
   * Добавляется:
   * - когда выбираешь заливку с фоном
   * - когда добавляешь новую строку в тексте
   * - когда входишь в режим редактирования старого
   * Перезаписывается (отчищается, потом добавляется):
   * - когда меняешь заливку
   * - когда меняешь шрифт при наличии нужной заливки
   * Отчищается:
   * - когда выходишь из редактирования
   * - когда удаляешь строку
   * - когда выбираешь без заливки
   * - когда сохраняешь оверлей
   * */
  const editableBackgroundObjects = ref<BackgroundObjects>([]);
  /**
   * Добавляется:
   * - когда выходишь из редактирования
   * Отчищается:
   * - когда входишь в режим редактирования старого
   * - когда сохраняешь оверлей
   * */
  const editableGroup = ref<ObjectsGroup>();
  /**
   * Добавляется:
   * - когда создаешь новую группу
   * - когда открываешь редактирования оверлея после того как закрыл и там были данные
   * Отчищается:
   * - когда удаляешь группу
   * */
  const allGroups = ref<GroupInstance[]>();

  /** Сохраняет текущую позицию группы, когда входишь в режим редактирования */
  const currentPositionTextObject = ref({ top: 0, left: 0, angle: 0 });
  const currentIdGroup = ref(0);
  const currentScale = ref<number | undefined>(1);

  /** Active text settings */
  const currentTextAlign = ref<TextAlign>(TextAlign.CENTER);
  const currentTextSize = ref(DEFAULT_FONT_SIZE);
  const currentFont = ref<FontsType>(FontsType.INTER);
  const currentColorIndex = ref(0);
  const currentFillType = ref<FillBackgroundType>(FillBackgroundType.EMPTY);
  const currentPadding = computed(() => currentTextSize.value * 0.66);

  const currentColorScheme = computed<ColorScheme>(() => ({
    color: colorList[currentColorIndex.value],
    colorIndex: currentColorIndex.value,
    fillType: currentFillType.value,
  }));

  const currentStyleScheme = computed<StyleScheme>(() => ({
    colorScheme: currentColorScheme.value,
    font: currentFont.value,
    textSize: currentTextSize.value,
    textAlign: currentTextAlign.value,
  }));

  const textObjectParams = computed(() => ({
    top: editableTextObject.value?.top ?? 0,
    left: editableTextObject.value?.left ?? 0,
    scaleX: editableTextObject.value?.scaleX ?? 1,
    scaleY: editableTextObject.value?.scaleY ?? 1,
    angle: editableTextObject.value?.angle ?? 0,
    width: editableTextObject.value?.width ?? 0,
    height: editableTextObject.value?.height ?? 0,
  }));

  let handleExitEditingText: () => void;
  let handleEnterEditGroup: (e: IEvent) => void;
  let addListeners: (object: fabric.Textbox | ObjectsGroup) => void;

  /** Отключает имеющиеся листенеры у текстового блока и созданной группы (текста и фонов) */
  const removeListeners = (object: fabric.Textbox | ObjectsGroup) => {
    if (object.type === ObjectType.TEXT_BLOCK) {
      object.off("changed");
      object.off("editing:exited");
      object.off("mousedown");
    } else if (object.type === ObjectType.GROUP) {
      object.off("mousedblclick");
    }
  };

  /** Красит текст и фоны */
  const paintingOverObjects = () => {
    if (!editableTextObject.value) return;
    onChangeColorsState(currentColorScheme.value, currentFont.value);

    editableTextObject.value.set({ fill: textColor.value });
    editableTextObject.value.setSelectionStyles({ fill: textColor.value });
    editableBackgroundObjects.value.forEach((item: any) => {
      if (currentFont.value === FontsType.INTER) {
        item._objects[0].set("fill", backgroundColors.value[0]);
        item._objects[1].set("fill", backgroundColors.value[1]);
      } else if (currentFont.value === FontsType.VOLLKORN) {
        item._objects[0].set("fill", backgroundColors.value[0]);
        item._objects[1].set("fill", backgroundColors.value[0]);
      } else {
        item.set("fill", backgroundColors.value[0]);
      }
    });
    canvasInstance.value?.renderAll();
  };

  /** Обнуляет переменные редактируемых фонов и удаляет с холста этиже фоны,
   * все равно они не нужны потому что создается группа
   * Вызывается при смене фонов */
  const removeOldBackground = () => {
    editableBackgroundObjects.value.forEach((item: any) => {
      canvasInstance.value?.remove(item);
    });
    editableBackgroundObjects.value = [];
    canvasInstance.value?.renderAll();
  };

  /** Удаляет лишние фоны, это может пригодиться, когда мы удаляем строки */
  const removeUnnecessaryBackground = () => {
    const editableBackgroundsLength = editableBackgroundObjects.value.length;
    if (editableBackgroundsLength > (editableTextObject.value?.textLines?.length ?? 0)) {
      setTimeout(() => {
        editableBackgroundObjects.value
          .splice(
            editableTextObject.value?.textLines?.length ?? 0,
            editableBackgroundsLength - (editableTextObject.value?.textLines?.length ?? 0)
          )
          .forEach(background => canvasInstance.value?.remove(background));
      }, 10);
    }
  };

  const changeBackgroundSize = (index: number) => {
    setTimeout(() => {
      if (!editableTextObject.value || !editableBackgroundObjects.value) return;

      const backgroundObject = editableBackgroundObjects.value[index] as fabric.Object;
      const lineHeight = editableTextObject.value?.__lineHeights[index];
      const lineWidth = editableTextObject.value?.__lineWidths[index];

      backgroundObject.set("fill", backgroundColors.value[0]);
      backgroundObject.set("top", textObjectParams.value.top + lineHeight * index * textObjectParams.value.scaleX - 8);
      backgroundObject.set("width", lineWidth + 32);
      backgroundObject.set("left", textObjectParams.value.left - currentPadding.value);
    }, 10);
  };

  const getExistingBackground = () => {
    const activeText = canvasInstance.value?.getActiveObject() as Textbox;
    if (allGroups.value?.length && activeText) {
      allGroups.value.forEach((groupInstance: GroupInstance) => {
        groupInstance.group._objects.forEach((item: any) => {
          if (item.name === activeText.name && item.type !== ObjectType.TEXT_BLOCK) {
            editableBackgroundObjects.value?.push(item);
          }
        });
      });
    }
  };

  const getLeftPosition = (backgroundWidth: number): number => {
    const leftPosition: Record<TextAlign, number> = {
      [TextAlign.LEFT]: currentPadding.value / 4,
      [TextAlign.CENTER]: (canvasInstance.value?.width ?? 0) / 2 - backgroundWidth / 2,
      [TextAlign.RIGHT]: (canvasInstance.value?.width ?? 0) - backgroundWidth - currentPadding.value / 4,
    };
    return leftPosition[currentTextAlign.value];
  };

  const createBackgroundObject = (
    type: ObjectType,
    objectSetting: Record<string, any>
  ): BackgroundFromRect | BackgroundFromPath | BackgroundFromText | undefined => {
    if (!editableTextObject.value || !editableBackgroundObjects.value) return;

    if (type === ObjectType.RECT_OBJ) {
      const backgroundWidth =
        editableTextObject.value?.__lineWidths[objectSetting.lineIndex] + currentPadding.value * 2;

      return new fabric.Rect({
        rx: objectSetting.borderRadius,
        ry: objectSetting.borderRadius,
        scaleX: textObjectParams.value.scaleX,
        scaleY: textObjectParams.value.scaleY,
        angle: textObjectParams.value.angle,
        top:
          textObjectParams.value.top +
          editableTextObject.value?.__lineHeights[0] * objectSetting.lineIndex -
          currentPadding.value / 2,
        left: getLeftPosition(backgroundWidth),
        width: objectSetting.lineTextLength ? backgroundWidth : 0,
        height: editableTextObject.value?.__lineHeights[objectSetting.lineIndex],
        fill: backgroundColors.value[0],
        opacity: objectSetting.opacity,
        stroke: "transparent",
        selectable: false,
      });
    } else if (type === ObjectType.PATH_OBJ) {
      const createPathBackground = (index: number) => {
        return new fabric.Path(objectSetting.parallelogramPath, {
          scaleX: textObjectParams.value.scaleX,
          scaleY: textObjectParams.value.scaleY,
          angle: textObjectParams.value.angle,
          top:
            textObjectParams.value.top +
            (editableTextObject.value?.__lineHeights[0] ?? 1) * objectSetting.lineIndex -
            objectSetting.top[index],
          left:
            getLeftPosition(editableTextObject.value?.__lineWidths[objectSetting.lineIndex] ?? 0) -
            (currentTextAlign.value === TextAlign.CENTER ? objectSetting.left[index] : index * 5),
          width: objectSetting.lineTextLength
            ? (editableTextObject.value?.__lineWidths[objectSetting.lineIndex] ?? 0) + currentPadding.value * 2
            : 0,
          height: editableTextObject.value?.__lineHeights[objectSetting.lineIndex],
          fill: backgroundColors.value[0],
          opacity: objectSetting.opacity[index],
          stroke: "transparent",
        });
      };

      const frontBackground = createPathBackground(0);
      const backBackground = createPathBackground(1);

      return new fabric.Group([backBackground, frontBackground]);
    } else if (type === ObjectType.TEXT_OBJ) {
      const createTextBackground = (index: number) => {
        return new fabric.Textbox(editableTextObject.value?.text ? editableTextObject.value.text : "", {
          left: currentPadding.value + 5 + objectSetting.left[index],
          top: textObjectParams.value.top + objectSetting.top[index],
          lineHeight: 1.5,
          fontFamily: editableTextObject.value?.fontFamily,
          fill: backgroundColors.value[index],
          hasControls: true,
          width: textObjectParams.value.width,
          fontSize: editableTextObject.value?.fontSize,
          fontWeight: 900,
          textAlign: editableTextObject.value?.textAlign,
          splitByGrapheme: true,
          scaleX: textObjectParams.value.scaleX,
          scaleY: textObjectParams.value.scaleY,
        });
      };

      const frontBackground = createTextBackground(0);
      const backBackground = createTextBackground(1);

      return new fabric.Group([backBackground, frontBackground]);
    }
  };

  /** Меняет ширину, тип фонов, создает новый фон */
  const changeBackground = () => {
    if (!canvasInstance.value || !editableTextObject.value || !editableBackgroundObjects.value) return;

    removeOldBackground();
    getExistingBackground();

    if (currentFillType.value === FillBackgroundType.EMPTY) {
      paintingOverObjects();
      return;
    }

    const fontOptions: Record<FontsType, { borderRadius: number; opacity: number }> = {
      [FontsType.COURIER_NEW]: { borderRadius: currentTextSize.value / 3, opacity: 0.7 },
      [FontsType.CAVEAT_BRUSH]: { borderRadius: currentTextSize.value, opacity: 1 },
      [FontsType.TINOS]: { borderRadius: 0, opacity: 1 },
      [FontsType.INTER]: { borderRadius: 0, opacity: 1 },
      [FontsType.VOLLKORN]: { borderRadius: 0, opacity: 1 },
    };

    editableTextObject.value.textLines.forEach((lineText, index) => {
      if (!editableTextObject.value) return;

      if (
        currentFont.value === FontsType.COURIER_NEW ||
        currentFont.value === FontsType.CAVEAT_BRUSH ||
        currentFont.value === FontsType.TINOS
      ) {
        if (!editableBackgroundObjects.value[index]) {
          const background = createBackgroundObject(ObjectType.RECT_OBJ, {
            lineIndex: index,
            lineTextLength: lineText.length,
            ...fontOptions[currentFont.value],
          });

          if (background) {
            editableBackgroundObjects.value[index] = background;
            (editableBackgroundObjects.value[index] as BackgroundFromRect).set("type", ObjectType.RECT_OBJ);
            canvasInstance.value?.add(editableBackgroundObjects.value[index]);
          }
        } else {
          changeBackgroundSize(index);
        }
      } else if (currentFont.value === FontsType.INTER) {
        const background = createBackgroundObject(ObjectType.TEXT_OBJ, {
          top: [currentTextSize.value / 12, currentTextSize.value / 6],
          left: [currentTextSize.value / 12, currentTextSize.value / 6],
          lineIndex: index,
          lineTextLength: lineText.length,
        });

        background && (editableBackgroundObjects.value[index] = background);
        (editableBackgroundObjects.value[index] as BackgroundFromText).set("type", ObjectType.TEXT_OBJ);
        canvasInstance.value?.add(editableBackgroundObjects.value[index]);
      } else if (currentFont.value === FontsType.VOLLKORN) {
        const widthCurrentLine = editableTextObject.value?.__lineWidths[index];
        const widthLineBackground = widthCurrentLine + currentPadding.value * 2;
        const heightLineBackground = currentTextSize.value + currentPadding.value;
        const differenceBetweenLines = heightLineBackground / 4;
        const pathInstructions: Record<string, { x: number; y: number }> = {
          topLine: { x: widthLineBackground, y: 0 },
          rightLine: { x: widthLineBackground - differenceBetweenLines, y: heightLineBackground },
          bottomLine: { x: -differenceBetweenLines, y: heightLineBackground },
        };

        /**
         * Все работает по аналогии с SVG path, но на всякий продублирую
         * M 0 0 это буквально установить точку начала на координаты 0 0
         * L и координаты x y рисует линию из предыдущей точки в точку с указанными координатами
         * */
        const parallelogramPath = lineText.length
          ? `M 0 0 ` +
            `L ${pathInstructions.topLine.x} ${pathInstructions.topLine.y} ` +
            `L ${pathInstructions.rightLine.x} ${pathInstructions.rightLine.y} ` +
            `L ${pathInstructions.bottomLine.x} ${pathInstructions.bottomLine.y} z`
          : "M 0 0 L 0 0 L 0 0 L 0 0 z";

        const background = createBackgroundObject(ObjectType.PATH_OBJ, {
          parallelogramPath,
          top: [currentTextSize.value / 4, currentTextSize.value / 2],
          left: [currentPadding.value, currentPadding.value + 5],
          opacity: [1, 0.5],
          lineIndex: index,
          lineTextLength: lineText.length,
        });

        background && (editableBackgroundObjects.value[index] = background);
        (editableBackgroundObjects.value[index] as BackgroundFromPath).set("type", ObjectType.PATH_OBJ);
        canvasInstance.value?.add(editableBackgroundObjects.value[index]);
      }

      (editableBackgroundObjects.value[index] as any).set("name", editableTextObject.value.name);
      editableTextObject.value?.bringToFront();
      canvasInstance.value?.renderAll();
    });

    removeUnnecessaryBackground();
    paintingOverObjects();
  };

  /** Центрирует текстовый блок по горизонтали и вертикали */
  const setTextPositionToCenterOfCanvas = (e: IEvent) => {
    if (!canvasInstance.value || !editableTextObject.value) return;

    if (canvasInstance.value.width && canvasInstance.value.height) {
      const newWidth = canvasInstance.value.width - (e.pointer?.x ?? 0) - (currentPadding.value + 5) * 2;
      editableTextObject.value?.set("width", newWidth);

      const newLeftPosition = canvasInstance.value.width / 2 - textObjectParams.value.width / 2;
      editableTextObject.value.set("left", newLeftPosition);

      const newTopPosition = canvasInstance.value.height / 2 - textObjectParams.value.height / 2;
      editableTextObject.value.set("top", newTopPosition);
    }
  };

  const setVariablesTextPosition = () => {
    if (!canvasInstance.value || editableTextObject.value?.text === "") return;

    startPositionTextObject.value.top = textObjectParams.value.top ?? currentPositionTextObject.value.top;
    startPositionTextObject.value.left = textObjectParams.value.left ?? currentPositionTextObject.value.left;
  };

  const resetToDefault = () => {
    currentPositionTextObject.value = { top: 0, left: 0, angle: 0 };
    currentScale.value = 1;
    editableTextObject.value?.exitEditing();
    editableTextObject.value = undefined;
    currentTextAlign.value = TextAlign.CENTER;
    currentTextSize.value = DEFAULT_FONT_SIZE;
    currentFont.value = FontsType.INTER;
    currentColorIndex.value = 0;
    currentFillType.value = FillBackgroundType.EMPTY;
    resetControllers();
  };

  /** добавляем группу и удаляем текст и его бэкграунды */
  const handleAddingGroupOnCanvas = () => {
    if (!canvasInstance.value || !editableGroup.value || !editableTextObject.value) return;

    canvasInstance.value.add(editableGroup.value);
    editableBackgroundObjects.value.forEach((item: any) => canvasInstance.value && canvasInstance.value.remove(item));
    editableBackgroundObjects.value = [];
    canvasInstance.value.remove(editableTextObject.value);
    canvasInstance.value.renderAll();
  };

  const deleteObjects = () => {
    if (!canvasInstance.value) return;

    const selectedGroup = canvasInstance.value?.getActiveObjects()[0] as ObjectsGroup | undefined;
    const groupIndex = allGroups.value?.findIndex(item => item.id === selectedGroup?.name) ?? -1;

    if (groupIndex > -1) allGroups.value?.splice(groupIndex, 1);

    canvasInstance.value.getActiveObjects().forEach(obj => {
      canvasInstance.value?.remove(obj);
    });
    canvasInstance.value.discardActiveObject().renderAll();
    showDeleteObjectIcon.value = false;
    setVariablesTextPosition();
  };

  const handleTextChange = (e: IEvent) => {
    if (!canvasInstance.value || !editableTextObject.value) return;
    fabric.util.clearFabricFontCache();

    /** вызываем центрирование каждый раз когда меняется текст, чтобы он всегда оставался по центру */
    setTextPositionToCenterOfCanvas(e);
    setVariablesTextPosition();

    if (!editableTextObject.value?.text) {
      editableBackgroundObjects.value.forEach((item: any) => {
        canvasInstance.value?.remove(item);
      });
    }

    if (
      currentFillType.value === FillBackgroundType.ACTIVE ||
      currentFillType.value === FillBackgroundType.ACTIVE_INVERT
    ) {
      changeBackground();
    }
  };

  /** обработка входа в режим редактирования */
  handleEnterEditGroup = (e: IEvent) => {
    const selectedGroup = e.target;

    /**
     * Получаем выделенный элемент, это группа, смотрим его name,
     * ищем в массиве уже созданных групп объект с таким же id
     * присваимваем в переменные объекты и стили
     * */

    const editableGroupInstance = allGroups.value?.find(item => item.id === selectedGroup?.name);
    if (editableGroupInstance && canvasInstance.value) {
      const setCurrentSettingsValue = (groupInstance: GroupInstance) => {
        currentScale.value = groupInstance.group.scaleX;
        currentPositionTextObject.value.top = groupInstance.group.top ?? 0;
        currentPositionTextObject.value.left = groupInstance.group.left ?? 0;
        currentPositionTextObject.value.angle = groupInstance.group.angle ?? 0;

        currentTextAlign.value = groupInstance.style.textAlign;
        currentTextSize.value = groupInstance.style.textSize;
        currentFont.value = groupInstance.style.font;
        currentColorIndex.value = groupInstance.style.colorScheme.colorIndex;
        currentFillType.value = groupInstance.style.colorScheme.fillType;
      };

      /** Центрируем группу перед тем как удалить, чтобы объекты текста и фонов также смогли ровно отцентрироваться */
      const returnGroupToCenter = () => {
        editableGroupInstance.group.set("scaleX", 1);
        editableGroupInstance.group.set("scaleY", 1);

        const newLeftPosition = (canvasInstance.value?.width ?? 2) / 2 - (editableGroupInstance.group.width ?? 2) / 2;
        editableGroupInstance.group.set("left", newLeftPosition);

        const newTopPosition = (canvasInstance.value?.height ?? 2) / 2 - (editableGroupInstance.group.height ?? 2) / 2;
        editableGroupInstance.group.set("top", newTopPosition);

        editableGroupInstance.group.set("angle", 0);
        editableGroupInstance.group._restoreObjectsState();
      };

      setCurrentSettingsValue(editableGroupInstance);
      returnGroupToCenter();

      canvasInstance.value.remove(editableGroupInstance.group);

      editableTextObject.value = editableGroupInstance.objects.textObject;
      editableBackgroundObjects.value = editableGroupInstance.objects.backgroundObjects;
      addListeners(editableTextObject.value);

      canvasInstance.value?.add(editableTextObject.value);
      editableBackgroundObjects.value.forEach(background => canvasInstance.value?.add(background));

      editableTextObject.value?.bringToFront();
      editableTextObject.value?.enterEditing(e.e as MouseEvent);
      showDeleteObjectIcon.value = false;
      canvasInstance.value.renderAll();
    }
  };

  const createGroup = () => {
    if (!canvasInstance.value || !editableTextObject.value) return;
    editableGroup.value = new fabric.Group([...editableBackgroundObjects.value, editableTextObject.value]);

    if (
      editableGroup.value &&
      currentPositionTextObject.value.top !== 0 &&
      currentPositionTextObject.value.left !== 0
    ) {
      editableGroup.value.set("top", currentPositionTextObject.value.top);
      editableGroup.value.set("left", currentPositionTextObject.value.left);
      editableGroup.value.set("angle", currentPositionTextObject.value.angle);
      editableGroup.value.set("scaleX", currentScale.value);
      editableGroup.value.set("scaleY", currentScale.value);
    }
    editableGroup.value.set("name", editableTextObject.value.name);
    editableGroup.value.set("type", ObjectType.GROUP);
    addListeners(editableGroup.value);

    if (!allGroups.value) allGroups.value = [];
    const groupIndex = allGroups.value.findIndex(item => item.id === editableGroup.value?.name);
    if (groupIndex > -1) {
      allGroups.value[groupIndex] = {
        id: editableTextObject.value.name ?? "",
        group: editableGroup.value,
        objects: { textObject: editableTextObject.value, backgroundObjects: editableBackgroundObjects.value },
        style: currentStyleScheme.value,
      };
    } else {
      allGroups.value?.push({
        id: editableTextObject.value.name ?? "",
        group: editableGroup.value,
        objects: { textObject: editableTextObject.value, backgroundObjects: editableBackgroundObjects.value },
        style: currentStyleScheme.value,
      });
    }
  };

  /** удаляет все объекты на основном канвасе, кроме текста и груп */
  const removeArtifacts = () => {
    const validObjectTypes = [ObjectType.TEXT_BLOCK, ObjectType.GROUP];
    const objects = canvasInstance.value?.getObjects();
    objects?.forEach((object: any) => {
      if (!validObjectTypes.includes(object.type)) {
        canvasInstance.value?.remove(object);
      }
    });
  };

  /** обработка выхода из режима редактирования */
  handleExitEditingText = () => {
    if (!canvasInstance.value || !editableTextObject.value) return;

    if (editableTextObject.value.text === "") {
      canvasInstance.value?.remove(editableTextObject.value);
      setVariablesTextPosition();
    } else {
      createGroup();
      handleAddingGroupOnCanvas();
    }

    removeListeners(editableTextObject.value);
    removeArtifacts();
    resetToDefault();
  };

  addListeners = (object: fabric.Textbox | ObjectsGroup) => {
    if (object.type === ObjectType.TEXT_BLOCK) {
      object.on("changed", handleTextChange);
      object.on("editing:exited", handleExitEditingText);
      object.on("mousedown", (e: IEvent) => {
        cursorIsChanged.value = true;
        (object as fabric.Textbox).setCursorByClick(e.e);
      });
    } else if (object.type === ObjectType.GROUP) {
      /**  будет ли очевидно людям, что чтобы отредактировать созданный текст, нужно будет дважды нажать на группу? */
      object.on("mousedblclick", handleEnterEditGroup);
    }
  };

  const createTextObject = () => {
    if (!canvasInstance.value) return;

    editableTextObject.value = new fabric.Textbox("", {
      top: canvasInstance.value?.height ?? 0 / 2,
      left: canvasInstance.value?.width ?? 0 / 2,
      scaleX: 1,
      scaleY: 1,
      fontSize: currentTextSize.value,
      fontWeight: 900,
      fontFamily: textFonts[currentFont.value].name,
      textAlign: currentTextAlign.value,
      lineHeight: 1.5,
      fill: textColor.value,
      hasControls: true,
      cursorColor: "white",
      splitByGrapheme: true,
      name: currentIdGroup.value.toString(),
      type: ObjectType.TEXT_BLOCK,
    });

    addListeners(editableTextObject.value);
    setVariablesTextPosition();
    paintingOverObjects();

    currentIdGroup.value++;
  };

  return {
    currentIdGroup,
    currentTextAlign,
    currentTextSize,
    currentFont,
    currentColorIndex,
    currentFillType,
    cursorIsChanged,
    editableTextObject,
    editableBackgroundObjects,
    editableGroup,
    allGroups,
    showDeleteObjectIcon,
    handleExitEditingText,
    addListeners,
    removeListeners,
    createTextObject,
    deleteObjects,
    changeBackground,
    paintingOverObjects,
    setTextPositionToCenterOfCanvas,
  };
}
